import { definedMap, withDefault } from "collection-utils";
import type {
    ActionPropertyDescriptor,
    AppDescriptionContext,
    ComponentBuilder,
    ComponentDescriptor,
    InteractiveComponentConfiguratorContext,
    PropertyDescriptor,
    RewritingComponentConfiguratorContext,
    SchemaInspector,
} from ".";
import type { SerializedComponent } from "../../common/components/SerializedApp";
import { getDocURL } from "../../common/docUrl";
import { getFeatureSetting } from "../../common/feature-settings";
import { assert } from "../../common/Support";
import { makeScreenContextForActions } from "../actions";
import { makeText, setColumnInRowForEditComponent } from "../builder-utils";
import type {
    ActionComponentDescription,
    ComponentKind,
    InputOutputTables,
    LegacyPropertyDescription,
    PropertyDescription,
    TableColumn,
    TableGlideType,
} from "../../common/description";
import {
    ActionKind,
    areTableNamesEqual,
    getDefaultContextColumnName,
    getTableColumn,
    getTableName,
    isComputedColumn,
    isPrimitiveType,
    isStringTypeOrStringTypeArray,
    makeEmptyComponentDescription,
    MutatingScreenKind,
    PropertyKind,
    SourceColumnKind,
} from "../../common/description";
import type { OverlayContentDescription } from "../overlay-utils";
import { defaultOverlayComponent, getOverlayComponents, getOverlayPropertyHandlers } from "../overlay-utils";
import type { LetBinding } from "../serialized-component";
import { getActionsForComponent } from "./component-utils";
import { makeImagePropertyDescriptor } from "./descriptor-utils";
import { ComponentHandlerBase } from "./handler";
import { Corners, ImageAspectRatio, ImageFitStyle, ImageGravity, Sizing } from "../../common/components/image-types";
import { makeDefaultActionDescriptor } from "./primitive";
import { makeImageHeightPropertyHandler } from "./property-handlers";
import { PropertySection } from "./property-sections";
import {
    getActionProperty,
    getEnumProperty,
    getSourceColumnProperty,
    makeColumnProperty,
    makeEnumProperty,
    makeStringProperty,
} from "./property-utils";
import { TextSize } from "./text-types";

const ComponentKindImage: ComponentKind = "image";

// FIXME: It's possible to change the assigned column to a user profile
// column, while the action is set to "Upload Image", but
// ##editedColumnsInUserProfile are not supported, but it won't make the
// action unset.  This used to lead to a crash:
// https://github.com/quicktype/glide/issues/11091

// Papercut to fix the issue: https://github.com/quicktype/glide/issues/11094

interface ImageComponentDescription extends ActionComponentDescription, OverlayContentDescription {
    readonly propertyName: LegacyPropertyDescription;
    // This is optional because we added it later.
    readonly size: LegacyPropertyDescription | undefined;
    readonly sizing: LegacyPropertyDescription | undefined;
    readonly style: PropertyDescription | undefined;
    readonly gravity: PropertyDescription | undefined;
}

function getImageGravity(desc: ImageComponentDescription): ImageGravity {
    return withDefault(getEnumProperty(desc.gravity), ImageGravity.Center);
}

function getImageComponentSizing(desc: ImageComponentDescription): Sizing {
    return withDefault(getEnumProperty(desc.sizing), Sizing.Cover);
}

function getImageComponentSize(desc: ImageComponentDescription): ImageAspectRatio {
    return withDefault(getEnumProperty(desc.size), ImageAspectRatio.Square);
}

function getImageComponentStyle(desc: ImageComponentDescription): ImageFitStyle {
    return withDefault(getEnumProperty(desc.style), ImageFitStyle.FullWidth);
}

function hasUploadImageAction(desc: ImageComponentDescription | undefined): boolean {
    const action = getActionProperty(desc?.actions);
    return action?.kind === ActionKind.UploadImage;
}

export class ImageComponentHandler extends ComponentHandlerBase<ImageComponentDescription> {
    constructor() {
        super(ComponentKindImage);
    }

    public getIsEditor(desc: ImageComponentDescription | undefined): boolean {
        return desc === undefined || hasUploadImageAction(desc);
    }

    public needValidation(desc: ImageComponentDescription): boolean {
        return hasUploadImageAction(desc);
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: ImageComponentDescription,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        validFlag: SerializedComponent | undefined,
        outputContext: SerializedComponent
    ): SerializedComponent {
        const { b } = builder;

        let showUploadButton = hasUploadImageAction(desc);

        let targetContext: SerializedComponent;
        let table: TableGlideType;
        if (showUploadButton) {
            targetContext = outputContext;
            table = tables.output;
        } else {
            targetContext = b.getContext;
            table = tables.input;
        }

        const { actions } = getActionsForComponent(this, desc, tables.input, builder, mutatingScreenKind);
        const enlarge = actions[0]?.kind === ActionKind.EnlargeImage;
        const { screenContext, lets: screenContextLets } = makeScreenContextForActions(
            builder,
            actions,
            tables,
            mutatingScreenKind
        );
        let onTap: SerializedComponent | undefined;
        if (!enlarge && !showUploadButton) {
            onTap = builder.makeSequenceFromActions(
                actions,
                tables,
                screenContext,
                outputContext,
                mutatingScreenKind,
                false
            );
        }

        let valueToModify: SerializedComponent | undefined;
        let setColumnLets: readonly LetBinding[] | undefined;
        let onChange: SerializedComponent | undefined;
        let isEnabled: SerializedComponent | undefined;

        const columnName = definedMap(getSourceColumnProperty(desc.propertyName), getDefaultContextColumnName);
        if (showUploadButton) {
            if (columnName !== undefined) {
                const setColumnsResult = setColumnInRowForEditComponent(
                    b,
                    showUploadButton ? mutatingScreenKind : undefined,
                    columnName,
                    b.getProperty(columnName, targetContext, table),
                    undefined,
                    tables,
                    undefined,
                    b.getContext,
                    { debounce: false }
                );

                if (setColumnsResult !== undefined) {
                    valueToModify = setColumnsResult.valueToModify;
                    setColumnLets = setColumnsResult.lets;
                    onChange = setColumnsResult.action;
                    isEnabled = setColumnsResult.isEnabled ?? b.constant(true);
                }
            }
        }

        if (columnName !== undefined && isEnabled === undefined) {
            // We need to ensure that we only upload to existent, non-computed
            // columns on the output table. But we also need to not break:
            // 1. Displaying images in forms
            // 2. Triggering other actions in forms based on image taps
            const column = getTableColumn(tables.output, columnName);
            if (
                column === undefined ||
                isComputedColumn(column) ||
                !areTableNamesEqual(getTableName(tables.input), getTableName(tables.output))
            ) {
                onChange = undefined;
                showUploadButton = false;
            }
        }

        if (valueToModify === undefined) {
            valueToModify = makeText(builder, desc.propertyName, tables.input, true, false, true);
            showUploadButton = false;
        }

        if (valueToModify === undefined) return b.emptyUI;

        const [topLeftOverlay, topRightOverlay, bottomLeftOverlay, bottomRightOverlay] = getOverlayComponents(
            desc,
            builder,
            tables,
            mutatingScreenKind,
            false,
            Corners.Square,
            TextSize.Medium
        );

        const ui = b.ui("Image", {
            image: valueToModify,
            size: b.constant(getImageComponentSize(desc)),
            sizing: b.constant(getImageComponentSizing(desc)),
            gravity: b.constant(getImageGravity(desc)),
            onChange,
            isEnabled: isEnabled ?? b.constant(false),
            style: b.constant(getImageComponentStyle(desc)),
            enlarge: b.constant(enlarge),
            onTap,
            showUploadButton: b.constant(showUploadButton),
            validFlag,
            topLeftOverlay,
            topRightOverlay,
            bottomLeftOverlay,
            bottomRightOverlay,
        });
        return b.let([...screenContextLets, ...(setColumnLets ?? [])], ui);
    }

    public getDescriptor(
        desc: ImageComponentDescription | undefined,
        _table: TableGlideType | undefined,
        _ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor {
        const imageHeightHandler = makeImageHeightPropertyHandler(ImageAspectRatio.Square, true, false, false);
        const uploadingImage = hasUploadImageAction(desc) || mutatingScreenKind === MutatingScreenKind.AddScreen;

        const properties: PropertyDescriptor[] = [
            makeImagePropertyDescriptor(
                "propertyName",
                "Image",
                "From URL",
                "Enter URL",
                true,
                false,
                uploadingImage,
                false,
                true,
                undefined,
                mutatingScreenKind
            ),
            imageHeightHandler,
            ...this.getBasePropertyDescriptors(),
        ];

        const size = definedMap(desc, d => getEnumProperty(d.size));
        if (size !== undefined && size !== ImageAspectRatio.Fit) {
            properties.push({
                kind: PropertyKind.Enum,
                property: { name: "sizing" },
                label: "Fill",
                menuLabel: "Size to",
                cases: [
                    {
                        value: Sizing.Cover,
                        label: "Fill the area",
                    },
                    {
                        value: Sizing.Contain,
                        label: "Show the whole image",
                    },
                ],
                defaultCaseValue: Sizing.Cover,
                accessoryTo: imageHeightHandler,
                section: PropertySection.Design,
                visual: "dropdown",
            });
        }

        properties.push({
            kind: PropertyKind.Enum,
            property: { name: "style" },
            label: "Style",
            menuLabel: "Image styling",
            cases: [
                {
                    value: ImageFitStyle.FullWidth,
                    label: "Edge to edge",
                },
                {
                    value: ImageFitStyle.Content,
                    label: "Inset with content",
                },
            ],
            defaultCaseValue: ImageFitStyle.FullWidth,
            section: PropertySection.Design,
            visual: "dropdown",
        });

        if (getFeatureSetting("imageGravity")) {
            properties.push({
                kind: PropertyKind.Enum,
                property: { name: "gravity" },
                label: "Crop Behavior",
                menuLabel: "Crop Behavior",
                cases: [
                    {
                        value: ImageGravity.Faces,
                        label: "Faces",
                        icon: "faceGravity",
                    },
                    {
                        value: ImageGravity.Center,
                        label: "Center",
                        icon: "centerGravity",
                    },
                ],
                defaultCaseValue: ImageGravity.Center,
                section: PropertySection.Design,
                visual: "small-images",
            });
        }

        properties.push(...getOverlayPropertyHandlers(undefined, mutatingScreenKind));

        return {
            name: "Image",
            description: "Display an image from a link",
            img: "componentImage",
            group: "Media",
            helpUrl: getDocURL("image"),
            properties,
        };
    }

    public getActionDescriptors(
        desc: ImageComponentDescription | undefined,
        table: TableGlideType | undefined,
        schema: SchemaInspector | undefined,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): readonly ActionPropertyDescriptor[] {
        const extraKinds: ActionKind[] = [ActionKind.EnlargeImage];
        if (mutatingScreenKind !== MutatingScreenKind.FormScreen && desc !== undefined && table !== undefined) {
            const columnName = definedMap(getSourceColumnProperty(desc.propertyName), getDefaultContextColumnName);
            if (columnName !== undefined) {
                const column = getTableColumn(table, columnName);
                if (column !== undefined && isPrimitiveType(column.type)) {
                    extraKinds.push(ActionKind.UploadImage);
                }
            }
        }
        return [makeDefaultActionDescriptor(schema, mutatingScreenKind, false, extraKinds)];
    }

    public newComponent(
        tables: InputOutputTables,
        usedColumns: ReadonlySet<TableColumn>,
        editedColumns: ReadonlySet<TableColumn>,
        iccc: InteractiveComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ImageComponentDescription | undefined {
        const desc = super.newComponent(tables, usedColumns, editedColumns, iccc, mutatingScreenKind);
        if (desc === undefined || mutatingScreenKind !== MutatingScreenKind.AddScreen) return desc;
        return {
            ...desc,
            actions: [
                {
                    kind: ActionKind.UploadImage,
                },
            ],
        };
    }

    public updateComponent(
        desc: ImageComponentDescription,
        updates: Partial<ImageComponentDescription>,
        tables: InputOutputTables,
        ccc: RewritingComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ImageComponentDescription {
        const newDesc = super.updateComponent(desc, updates, tables, ccc, mutatingScreenKind);
        // The Upload Image action is incompatible with anything other than
        // columns from the default context, so if the column changes to a
        // non-default context column, we unset the Upload Image action.  See
        // https://github.com/quicktype/glide/issues/11094
        if (hasUploadImageAction(newDesc)) {
            const sc = getSourceColumnProperty(newDesc.propertyName);
            if (sc?.kind !== SourceColumnKind.DefaultContext) {
                return {
                    ...newDesc,
                    actions: undefined,
                };
            }
        }
        return newDesc;
    }

    public static defaultComponent(column: TableColumn): ImageComponentDescription {
        assert(isStringTypeOrStringTypeArray(column.type));
        return {
            ...makeEmptyComponentDescription(ComponentKindImage),
            propertyName: makeColumnProperty(column.name),
            size: makeEnumProperty(ImageAspectRatio.Square),
            sizing: makeEnumProperty(Sizing.Cover),
            style: makeEnumProperty(ImageFitStyle.FullWidth),
            gravity: makeEnumProperty(ImageGravity.Center),
            actions: [],
            ...defaultOverlayComponent(),
        };
    }

    public static defaultComponentFromURL(url: string): ImageComponentDescription {
        return {
            ...makeEmptyComponentDescription(ComponentKindImage),
            propertyName: makeStringProperty(url),
            size: makeEnumProperty(ImageAspectRatio.Square),
            sizing: makeEnumProperty(Sizing.Cover),
            style: makeEnumProperty(ImageFitStyle.FullWidth),
            gravity: makeEnumProperty(ImageGravity.Center),
            actions: [],
            ...defaultOverlayComponent(),
        };
    }
}
